export type EnvConfig = {
  production: boolean;
  rowcalCompanyId: number;
  enableVendorAppRedirect?: boolean;
  viveApiUrl: string;
  vendorsAppUrl?: string;
  postHog?: {
    host: string;
    publicKey: string;
  };
  datadog?: {
    applicationId: string;
    clientToken: string;
    site: string;
    service: string;
    env: string;
  };
  stripe: {
    US: { publicKey: string };
    CA: { publicKey: string };
  };
};

export enum DeployEnvironment {
  // DEVELOPER environments
  TESTING = 'testing', // Documented here, only used with API jest unit tests
  DEVELOPMENT = 'development',

  // AWS ECS environments
  STAGING = 'staging',
  UAT = 'uat',
  PRODUCTION = 'production'
}

class Config {
  // Retrieves the current environment based on domain name
  public getDeployEnvironment(): DeployEnvironment {
    const hostName = window.location.hostname;
    switch (hostName) {
      case 'localhost':
      case '127.0.0.1':
        return DeployEnvironment.DEVELOPMENT;

      case 'staging.getvived.com':
      case 'app.staging.getvived.com':
        return DeployEnvironment.STAGING;

      case 'uat.getvived.com':
      case 'app.uat.getvived.com':
        return DeployEnvironment.UAT;

      case 'production.getvived.com':
      case 'beta.getvived.com':
      case 'app.getvived.com':
        return DeployEnvironment.PRODUCTION;
    }

    console.log(`Unknown domain ${hostName}, unable to get environment`);
    throw new Error(`Unknown domain ${hostName}, unable to get environment`);
  }

  public getEnvironmentConfig(): EnvConfig {
    const environment = this.getDeployEnvironment();
    switch (environment) {
      case DeployEnvironment.DEVELOPMENT:
        return {
          production: false,
          rowcalCompanyId: 1,
          viveApiUrl: 'http://localhost:3000',
          vendorsAppUrl: 'http://localhost:4201',
          enableVendorAppRedirect: true,
          stripe: {
            US: {
              publicKey:
                'pk_test_51NDpygAaXZ6tFmcdM1CNIX1Gpk0YdKi582r1oF6155idztEXxuJfPqwH1IMN8az9xjACzRnVYVxce43HMBFPTTzV00trmKv4bi'
            },
            CA: {
              publicKey:
                'pk_test_51NDpzuAGSa7l4LXHTTKKGcUFcgZfH1JR2XLa6YulJZIBQb6Zbxkijp96vbHvdIWorh2ARQimKdbs5moLM34vgfdI00s4ifJACL'
            }
          }
        };

      case DeployEnvironment.STAGING:
        return {
          production: true,
          rowcalCompanyId: 14,
          enableVendorAppRedirect: true,
          viveApiUrl: 'https://api.staging.getvived.com',
          vendorsAppUrl: 'https://vendors.staging.getvived.com',
          postHog: {
            host: 'https://us.i.posthog.com',
            publicKey: 'phc_7NwKsnfINZgC3MGcS5W5GtXXA7HnOq51r9EutVP676I'
          },
          datadog: {
            // Client-side RUM tokens are safe to expose (not secrets).
            applicationId: '23dc178d-1f30-45c4-884c-8312691ec4df',
            clientToken: 'puba435240a5d21f75dd4addfc27e23b613',
            site: 'datadoghq.com',
            service: 'vive_legacy_app',
            env: 'staging'
          },
          stripe: {
            US: {
              publicKey:
                'pk_test_51NDpygAaXZ6tFmcdM1CNIX1Gpk0YdKi582r1oF6155idztEXxuJfPqwH1IMN8az9xjACzRnVYVxce43HMBFPTTzV00trmKv4bi'
            },
            CA: {
              publicKey:
                'pk_test_51NDpzuAGSa7l4LXHTTKKGcUFcgZfH1JR2XLa6YulJZIBQb6Zbxkijp96vbHvdIWorh2ARQimKdbs5moLM34vgfdI00s4ifJACL'
            }
          }
        };

      case DeployEnvironment.UAT:
        return {
          production: true,
          rowcalCompanyId: 14,
          enableVendorAppRedirect: true,
          viveApiUrl: 'https://api.uat.getvived.com',
          vendorsAppUrl: 'https://vendors.uat.getvived.com',
          postHog: {
            host: 'https://us.i.posthog.com',
            publicKey: 'phc_7NwKsnfINZgC3MGcS5W5GtXXA7HnOq51r9EutVP676I'
          },
          datadog: {
            // Client-side RUM tokens are safe to expose (not secrets).
            applicationId: '23dc178d-1f30-45c4-884c-8312691ec4df',
            clientToken: 'puba435240a5d21f75dd4addfc27e23b613',
            site: 'datadoghq.com',
            service: 'vive_legacy_app',
            env: 'uat'
          },
          stripe: {
            US: {
              publicKey:
                'pk_test_51NDpygAaXZ6tFmcdM1CNIX1Gpk0YdKi582r1oF6155idztEXxuJfPqwH1IMN8az9xjACzRnVYVxce43HMBFPTTzV00trmKv4bi'
            },
            CA: {
              publicKey:
                'pk_test_51NDpzuAGSa7l4LXHTTKKGcUFcgZfH1JR2XLa6YulJZIBQb6Zbxkijp96vbHvdIWorh2ARQimKdbs5moLM34vgfdI00s4ifJACL'
            }
          }
        };

      case DeployEnvironment.PRODUCTION:
        return {
          production: true,
          rowcalCompanyId: 253,
          viveApiUrl: 'https://api.getvived.com',
          vendorsAppUrl: 'https://vendors.getvived.com',
          enableVendorAppRedirect: true,
          postHog: {
            host: 'https://us.i.posthog.com',
            publicKey: 'phc_7NwKsnfINZgC3MGcS5W5GtXXA7HnOq51r9EutVP676I'
          },
          datadog: {
            // Client-side RUM tokens are safe to expose (not secrets).
            applicationId: '23dc178d-1f30-45c4-884c-8312691ec4df',
            clientToken: 'puba435240a5d21f75dd4addfc27e23b613',
            site: 'datadoghq.com',
            service: 'vive_legacy_app',
            env: 'production'
          },
          stripe: {
            US: {
              publicKey:
                'pk_live_51NDpygAaXZ6tFmcdymlVHagkuu2zhPNXjdtpAXFttiSK1QNybmLPgkjK7aZpOOs83Fqwpw7BG4muDengZvVyNk4s00xKc3YhOR'
            },
            CA: {
              publicKey:
                'pk_live_51NDpzuAGSa7l4LXHag3pV47dp2UPFm7uCYBIv7gMPrkwDN6RPWiqWtSYBV2hW6tOTq4fYLZ6kH9qLgQmt6C0mYti00AwXOq11e'
            }
          }
        };
    }

    throw new Error(`Unknown environment ${environment}, unable to get config`);
  }
}

const config = new Config();
export default config;
